"use client";

import { createContext, useContext, useState, type ReactNode } from "react";

export type AuthModalMode = "login" | "signup";

export interface OpenAuthModalOptions {
  mode?: AuthModalMode;
  /** Kontextspezifischer Grund, warum gerade ein Konto noetig ist (Erik 2026-07-17:
   * Konto-Gate fuer Watchlist/Depot/Alerts/PDF-Export) - ersetzt im Signup-Modus den
   * generischen Beschreibungstext in AuthModal. Ohne Angabe bleibt der generische Text. */
  reason?: string;
}

type AuthModalContextValue = {
  isOpen: boolean;
  initialMode: AuthModalMode;
  reason: string | null;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  closeAuthModal: () => void;
};

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialMode, setInitialMode] = useState<AuthModalMode>("login");
  const [reason, setReason] = useState<string | null>(null);

  return (
    <AuthModalContext.Provider
      value={{
        isOpen,
        initialMode,
        reason,
        openAuthModal: (options) => {
          setInitialMode(options?.mode ?? "login");
          setReason(options?.reason ?? null);
          setIsOpen(true);
        },
        closeAuthModal: () => setIsOpen(false),
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal muss innerhalb von AuthModalProvider verwendet werden");
  }
  return ctx;
}
